// react
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useForm } from '@tanstack/react-form';
// tanstack
import { createFileRoute, useParams } from '@tanstack/react-router';

// material ui icons
import DeleteIcon from '@mui/icons-material/Delete';

// material ui
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

// api
import type { ReadUserRolesDto } from '@/api/generated/recalc/endpoints/v1/accounts';
import {
  useGetAccountByIdQuery,
  useGrantUserRoleToAccountMutation,
  useRemoveUserRoleFromAccountMutation,
} from '@/api/recalc/endpoints/accountsApiSlice';

// components
import { setSelectedAccount } from '@/redux/selectedAccountSlice';
import { showSnackbar } from '@/redux/snackbarSlice';

const normalizeRole = (roles: string[] = []): string => {
  if (roles.includes('Administrator')) return 'Administrator';
  if (roles.includes('ReadOnly')) return 'Read Only';
  if (roles.includes('User')) return 'User';
  return 'User';
};

// shared components
import { AdministratorOnly } from '@/components/access-control';

// components
import ErrorBoundary from '@/routes/-ErrorBoundary.tsx';

export const Route = createFileRoute('/$accountId/_user/account/users')({
  component: RouteComponent,
  errorComponent: ErrorBoundary,
});

function RouteComponent() {
  const { t } = useTranslation('account');
  const dispatch = useDispatch();
  const { accountId } = useParams({ strict: false });
  const { data: account } = useGetAccountByIdQuery(accountId);

  const [grantUserRoleToAccount, { isLoading: isGranting }] =
    useGrantUserRoleToAccountMutation();
  const [removeUserRoleFromAccount, { isLoading: isRemoving }] =
    useRemoveUserRoleFromAccountMutation();
  const [rolesChanged, setRolesChanged] = useState(false);
  const [userRolesState, setUserRolesState] = useState<string[]>([]);

  // Extract users from account data
  const users: ReadUserRolesDto[] | undefined = account?.UserRoles;

  // Initialize roles state
  React.useEffect(() => {
    setUserRolesState((users ?? []).map((u) => normalizeRole(u.Roles || [])));
    setRolesChanged(false);
  }, [users]);

  // Add user form
  const addUserForm = useForm({
    defaultValues: {
      email: '',
      role: 'User',
    },
    onSubmit: async ({ value }) => {
      if (!accountId) return;

      const role = value.role === 'Read Only' ? 'ReadOnly' : value.role;
      const result = await grantUserRoleToAccount({
        accountId,
        email: value.email,
        role,
      });

      if (!('error' in result)) {
        // Update form state
        addUserForm.reset();

        // Update Redux state
        dispatch(
          setSelectedAccount({
            ...account,
            UserRoles: [
              ...(account?.UserRoles || []),
              { User: { Email: value.email }, Roles: [role] },
            ],
          }),
        );

        dispatch(
          showSnackbar({
            message:
              t?.('users.messages.userAdded') || 'User added successfully',
            severity: 'success',
          }),
        );
      }
      // Error handled by apiErrorMiddleware (snackbar + App Insights).
    },
  });

  const handleDeleteUser = async (user: ReadUserRolesDto) => {
    if (!accountId) return;
    const userEmail = user.User?.Email;
    if (!userEmail) return;

    const normalizedRole = normalizeRole(user.Roles || []);
    const role = normalizedRole === 'Read Only' ? 'ReadOnly' : normalizedRole;
    const result = await removeUserRoleFromAccount({
      accountId,
      email: userEmail,
      role,
    });

    if (!('error' in result)) {
      dispatch(
        setSelectedAccount({
          ...account,
          UserRoles: (account?.UserRoles || []).filter(
            (ur: ReadUserRolesDto) =>
              ur.User?.Email?.toLowerCase() !== userEmail.toLowerCase(),
          ),
        }),
      );

      dispatch(
        showSnackbar({
          message:
            t?.('users.messages.userRemoved') || 'User removed successfully',
          severity: 'success',
        }),
      );
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  const handleRoleChange = (idx: number, newRole: string) => {
    const newRoles = [...userRolesState];
    newRoles[idx] = newRole;
    setUserRolesState(newRoles);
    setRolesChanged(true);
  };

  const handleSaveRoles = async () => {
    if (!accountId) return;

    const normalize = (r: string) => (r === 'Read Only' ? 'ReadOnly' : r);
    const changes = (users ?? [])
      .map((u, idx) => ({
        email: u.User?.Email || '',
        current: normalize(normalizeRole(u.Roles || [])),
        next: normalize(userRolesState[idx]),
      }))
      .filter((c) => c.next && c.email && c.next !== c.current);

    if (changes.length > 0) {
      const results = await Promise.all(
        changes.map((c) =>
          grantUserRoleToAccount({
            accountId,
            email: c.email,
            role: c.next,
          }),
        ),
      );

      if (results.some((r) => 'error' in r)) {
        // Errors handled by apiErrorMiddleware (snackbar + App Insights).
        return;
      }
    }

    setRolesChanged(false);

    const updatedUserRoles = (account?.UserRoles || []).map(
      (ur: ReadUserRolesDto) => {
        const emailLower = ur.User?.Email?.toLowerCase();
        const idx = (users ?? []).findIndex(
          (u) => u.User?.Email?.toLowerCase() === emailLower,
        );
        if (idx === -1) return ur;
        const newRole = normalize(userRolesState[idx]);
        return { ...ur, Roles: [newRole] };
      },
    );

    dispatch(setSelectedAccount({ ...account, UserRoles: updatedUserRoles }));

    dispatch(
      showSnackbar({
        message:
          t?.('users.messages.rolesUpdated') ||
          'User roles updated successfully',
        severity: 'success',
      }),
    );
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  if (!account?.AccountId) {
    return (
      <Box p={2}>
        <Typography color="text.secondary" data-testid="no-account">
          {t?.('messages.noAccount') || 'No account found'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Add User Form */}
      <AdministratorOnly>
        <Card>
          <CardHeader
            title={t?.('users.title', { accountName: account.Name })}
            slotProps={{
              title: { variant: 'h6' }
            }}
          />
          <Divider />
          <CardContent>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                addUserForm.handleSubmit();
              }}
            >
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <addUserForm.Field
                  name="email"
                  validators={{
                    onChange: ({ value }) => {
                      if (!value)
                        return (
                          t?.('validation.emailRequired') || 'Email is required'
                        );
                      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                      if (!emailRegex.test(value)) {
                        return (
                          t?.('validation.invalidEmail') ||
                          'Please enter a valid email address'
                        );
                      }
                      if (
                        (users ?? []).some(
                          (u) =>
                            u.User?.Email?.toLowerCase() ===
                            value.toLowerCase(),
                        )
                      ) {
                        return (
                          t?.('validation.userExists') ||
                          'User already exists in this account'
                        );
                      }
                      return undefined;
                    },
                  }}
                >
                  {(field) => (
                    <TextField
                      label={t?.('users.input.email') || 'Email'}
                      type="email"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      error={!!field.state.meta.errors.length}
                      helperText={field.state.meta.errors.join(', ')}
                      sx={{ flex: 1 }}
                      data-testid="add-user-email"
                    />
                  )}
                </addUserForm.Field>

                <addUserForm.Field name="role">
                  {(field) => (
                    <TextField
                      select
                      sx={{ minWidth: 150 }}
                      label={t?.('users.input.role') || 'Vælg rolle'}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      data-testid="add-user-role"
                    >
                      <MenuItem value="User">
                        {t?.('users.roles.user') || 'User'}
                      </MenuItem>
                      <MenuItem value="Read Only">
                        {t?.('users.roles.readonly') || 'Read Only'}
                      </MenuItem>
                      <MenuItem value="Administrator">
                        {t?.('users.roles.admin') || 'Administrator'}
                      </MenuItem>
                    </TextField>
                  )}
                </addUserForm.Field>

                <Box>
                  <Button
                    type="submit"
                    variant="contained"
                    disableElevation
                    disabled={isGranting || !addUserForm.state.isValid}
                    data-testid="add-user-button"
                    sx={{ height: 55 }}
                  >
                    {t?.('users.buttons.add') || 'Add User'}
                  </Button>
                </Box>
              </Box>
            </form>
          </CardContent>
        </Card>
      </AdministratorOnly>

      {/* Users Table */}
      <Box
        sx={{
          py: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {rolesChanged && (
          <AdministratorOnly>
            <Button
              variant="contained"
              onClick={handleSaveRoles}
              disabled={isGranting}
              data-testid="save-roles-button"
            >
              {t?.('users.messages.saveChanges') || 'Save Changes'}
            </Button>
          </AdministratorOnly>
        )}
      </Box>

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: 2 }}
      >
        <Table size="small" aria-label="a dense table">
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography variant="subtitle2" fontWeight="bold">
                  {t?.('users.table_headers.email') || 'Email'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight="bold">
                  {t?.('users.table_headers.status') || 'Status'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight="bold">
                  {t?.('users.table_headers.role') || 'Role'}
                </Typography>
              </TableCell>
              <AdministratorOnly fallback={null}>
                <TableCell align="center">
                  <Typography variant="subtitle2" fontWeight="bold">
                    {t?.('users.table_headers.actions') || 'Actions'}
                  </Typography>
                </TableCell>
              </AdministratorOnly>
            </TableRow>
          </TableHead>
          <TableBody>
            {(users ?? []).map((user, index) => (
              <TableRow
                key={user.User?.Email || index}
                sx={{
                  height: 64,
                  '&:last-child td, &:last-child th': { border: 0 },
                }}
              >
                <TableCell>{user.User?.Email || ''}</TableCell>
                <TableCell>
                  {user.User?.Active ? (
                    <Tooltip
                      title={
                        t?.('users.activeTooltip') || 'This user is active'
                      }
                    >
                      <Chip
                        label={t?.('users.active')}
                        color="success"
                        size="small"
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip
                      title={
                        t?.('users.emailSentTooltip') ||
                        'An email has been sent to this user'
                      }
                    >
                      <Chip label={t?.('users.emailSent')} size="small" />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  <AdministratorOnly
                    fallback={
                      <Typography>{normalizeRole(user.Roles || [])}</Typography>
                    }
                  >
                    <TextField
                      select
                      size="small"
                      label={t?.('users.input.role') || 'Rolle'}
                      sx={{ minWidth: 150 }}
                      value={
                        userRolesState[index] || normalizeRole(user.Roles || [])
                      }
                      onChange={(e) => handleRoleChange(index, e.target.value)}
                      data-testid={`user-role-${index}`}
                    >
                      <MenuItem value="User">
                        {t?.('users.roles.user') || 'User'}
                      </MenuItem>
                      <MenuItem value="Read Only">
                        {t?.('users.roles.readonly') || 'Read Only'}
                      </MenuItem>
                      <MenuItem value="Administrator">
                        {t?.('users.roles.admin') || 'Administrator'}
                      </MenuItem>
                    </TextField>
                  </AdministratorOnly>
                </TableCell>
                <AdministratorOnly fallback={null}>
                  <TableCell align="center">
                    <Tooltip
                      placement="top"
                      title={t?.('users.buttons.remove') || 'Remove user'}
                    >
                      <IconButton
                        onClick={() => handleDeleteUser(user)}
                        disabled={isRemoving}
                        data-testid={`delete-user-${index}`}
                        sx={{
                          '&:hover': { color: 'error.main' },
                          transition: 'color 0.2s',
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </AdministratorOnly>
              </TableRow>
            ))}
            {(users ?? []).length === 0 && (
              <TableRow>
                <TableCell align="center">
                  <Typography color="text.secondary">
                    {t?.('users.noUsers') || 'No users found'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
